import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import { useNavigate } from "react-router-dom";
import { navbarIconButton } from "examples/Navbars/DashboardNavbar/styles";
const NotificationBell = ({ iconsStyle }) => {
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const pollingRef = useRef(null);

  const getUserData = () => {
    try {
      const user = JSON.parse(sessionStorage.getItem("user"));

      if (!user) {
        return null;
      }

      const clientId = user.client_id;
      const collegeId = sessionStorage.getItem("selected_college_id") || user.college_id;

      const userId = user.user_id;

      const roleId = sessionStorage.getItem("selected_role_id") || user.role_id;

      if (!clientId || !collegeId || !userId || !roleId) {
        return null;
      }

      return {
        clientId,
        collegeId,
        userId,
        roleId,
      };
    } catch (error) {
      console.error("Failed to read notification user data:", error);
      return null;
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const user = getUserData();

      if (!user) {
        setUnreadCount(0);
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/notifications/${user.clientId}/${user.collegeId}/${user.userId}/${user.roleId}/unread-count`
      );

      const data = await response.json();

      if (data.success) {
        setUnreadCount(Number(data.unreadCount || 0));
      }
    } catch (error) {
      console.error("Failed to fetch notification count:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const user = getUserData();

      if (!user) {
        setNotifications([]);
        return;
      }

      setLoading(true);

      const response = await fetch(
        `${API_BASE_URL}/api/notifications/${user.clientId}/${user.collegeId}/${user.userId}/${user.roleId}?limit=20&offset=0`
      );

      const data = await response.json();

      if (data.success) {
        setNotifications(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnreadCount();

    // Refresh unread count periodically.
    pollingRef.current = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  /*
   * When college or role changes in DashboardNavbar,
   * refresh notification count.
   */
  useEffect(() => {
    const handleRBACChange = () => {
      fetchUnreadCount();

      if (anchorEl) {
        fetchNotifications();
      }
    };

    window.addEventListener("rbac-permissions-changed", handleRBACChange);

    return () => {
      window.removeEventListener("rbac-permissions-changed", handleRBACChange);
    };
  }, [anchorEl]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markAsRead = async (notificationId) => {
    try {
      const user = getUserData();

      if (!user) {
        return;
      }

      await fetch(`${API_BASE_URL}/api/notifications/${user.clientId}/mark-read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          notificationId,
          userId: user.userId,
        }),
      });

      setNotifications((prev) =>
        prev.map((item) =>
          String(item.notification_id) === String(notificationId)
            ? {
                ...item,
                is_read: 1,
              }
            : item
        )
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const user = getUserData();

      if (!user) {
        return;
      }

      await fetch(`${API_BASE_URL}/api/notifications/${user.clientId}/mark-all-read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          collegeId: user.collegeId,
          userId: user.userId,
          roleId: user.roleId,
        }),
      });

      setNotifications((prev) =>
        prev.map((item) => ({
          ...item,
          is_read: 1,
        }))
      );

      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    const notificationId = notification.notification_id;

    if (!Number(notification.is_read)) {
      await markAsRead(notificationId);
    }

    handleClose();

    /*
     * route_path is stored by the backend.
     *
     * Example:
     * /admin/fee-heads
     * /admin/fee-books
     * /admin/fee-books/approval/125
     */
    if (notification.route_path) {
      navigate(notification.route_path);
    }
  };

  const handleViewAll = () => {
    handleClose();
    navigate("/admin/notifications");
  };

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "";
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const now = new Date();

    const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffSeconds < 60) {
      return "Just now";
    }

    const diffMinutes = Math.floor(diffSeconds / 60);

    if (diffMinutes < 60) {
      return `${diffMinutes} min ago`;
    }

    const diffHours = Math.floor(diffMinutes / 60);

    if (diffHours < 24) {
      return `${diffHours} hr ago`;
    }

    const diffDays = Math.floor(diffHours / 24);

    if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    }

    return date.toLocaleDateString();
  };

  const getPriorityIcon = (priority, isRead) => {
    if (isRead) {
      return "notifications_none";
    }

    switch (priority) {
      case "CRITICAL":
        return "priority_high";

      case "HIGH":
        return "warning";

      case "LOW":
        return "info";

      default:
        return "notifications_active";
    }
  };

  return (
    <>
      <IconButton
        size="small"
        disableRipple
        color="inherit"
        sx={navbarIconButton}
        aria-label="notifications"
        onClick={handleOpen}
      >
        <Badge
          badgeContent={unreadCount > 99 ? "99+" : unreadCount}
          color="error"
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "10px",
              minWidth: 18,
              height: 18,
              padding: "0 4px",
              fontWeight: 700,
            },
          }}
        >
          <Icon sx={iconsStyle}>notifications</Icon>
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: {
              xs: "calc(100vw - 20px)",
              sm: 400,
            },
            maxWidth: 400,
            maxHeight: 560,
            mt: 1,
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "rgb(123, 31, 162)",
            color: "white",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                backgroundColor: "rgb(123, 31, 162)",
                color: "#FFFFFF",
                px: 1,
                py: 0.5,
                borderRadius: "4px",
              }}
            >
              Notifications
            </Typography>

            {unreadCount > 0 && (
              <Typography
                sx={{
                  fontSize: "12px",
                  opacity: 0.9,
                  backgroundColor: "rgb(123, 31, 162)",
                  color: "#FFFFFF",
                }}
              >
                {unreadCount} unread notification
                {unreadCount !== 1 ? "s" : ""}
              </Typography>
            )}
          </Box>

          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={markAllAsRead}
              sx={{
                color: "#FFFFFF",
                fontSize: "11px",
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "rgb(123, 31, 162)",
                "&:hover": {
                  backgroundColor: "#4A148C",
                  color: "#FFFFFF",
                },
              }}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {/* CONTENT */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 5,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box
            sx={{
              py: 5,
              px: 2,
              textAlign: "center",
            }}
          >
            <Icon
              sx={{
                fontSize: 45,
                color: "text.disabled",
              }}
            >
              notifications_none
            </Icon>

            <Typography
              sx={{
                mt: 1,
                fontSize: "16px",
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              No notifications
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: 410,
              overflowY: "auto",
            }}
          >
            {notifications.map((notification) => {
              const isRead = Number(notification.is_read) === 1;

              return (
                <React.Fragment key={String(notification.notification_id)}>
                  <MenuItem
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      whiteSpace: "normal",
                      px: 2,
                      py: 1.5,
                      alignItems: "flex-start",
                      backgroundColor: isRead ? "white" : "#eaf4ff",

                      "&:hover": {
                        backgroundColor: isRead ? "#f5f5f5" : "#dceeff",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 38,
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.5,
                        backgroundColor: isRead ? "#eeeeee" : "rgb(123, 31, 162)",
                        color: isRead ? "#757575" : "#FFFFFF",
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }}>
                        {getPriorityIcon(notification.priority, isRead)}
                      </Icon>
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: isRead ? 500 : 700,
                          color: "#212121",
                          lineHeight: 1.35,
                        }}
                      >
                        {notification.title}
                      </Typography>

                      {notification.message && (
                        <Typography
                          sx={{
                            mt: 0.4,
                            fontSize: "12px",
                            color: "#616161",
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {notification.message}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.7,
                        }}
                      >
                        {notification.module_name && (
                          <Typography
                            sx={{
                              fontSize: "10px",
                              color: "#1976d2",
                              fontWeight: 600,
                            }}
                          >
                            {notification.module_name}
                          </Typography>
                        )}

                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: "#9e9e9e",
                            fontWeight: 500,
                          }}
                        >
                          {formatDate(notification.created_date)}
                        </Typography>
                      </Box>
                    </Box>

                    {!isRead && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#1976d2",
                          mt: 0.7,
                          ml: 1,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </MenuItem>

                  <Divider />
                </React.Fragment>
              );
            })}
          </Box>
        )}

        {/* FOOTER */}
        <Box
          sx={{
            p: 1,
            textAlign: "center",
            borderTop: "1px solid #eeeeee",
          }}
        >
          <Button
            fullWidth
            onClick={handleViewAll}
            sx={{
              color: "#1976d2",
              fontWeight: 700,
              fontSize: "13px",
              textTransform: "none",
            }}
          >
            View All Notifications
          </Button>
        </Box>
      </Menu>
    </>
  );
};

NotificationBell.propTypes = {
  iconsStyle: PropTypes.func.isRequired,
};

export default NotificationBell;
